/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { globalRef } from '@amazon/vinyl-util'

type Method = (...args: never[]) => unknown

/**
 * A module-scope object whose instance lives in a `globalRef`, so a
 * global-registry reset (between tests) disposes it and the next use builds a
 * fresh one from `init`. The returned proxy is a stable binding to export in
 * place of `new X()`: each property access goes to the instance current at
 * that moment. Methods come back bound to the instance they were read from, so
 * async work in flight across a reset stays on the instance that started it.
 */
export function globalInstance<T extends object>(init: () => T): T {
    const ref = globalRef(init)
    const bound = new WeakMap<T, WeakMap<Method, Method>>()
    const bindTo = (instance: T, fn: Method): Method => {
        let methods = bound.get(instance)
        if (!methods) bound.set(instance, (methods = new WeakMap()))
        let method = methods.get(fn)
        if (!method) methods.set(fn, (method = fn.bind(instance)))
        return method
    }
    return new Proxy({} as T, {
        get(_, key) {
            const instance = ref.value
            const value: unknown = Reflect.get(instance, key)
            return typeof value === 'function' && !Object.hasOwn(instance, key)
                ? bindTo(instance, value as Method)
                : value
        },
        set: (_, key, value) => Reflect.set(ref.value, key, value),
        has: (_, key) => Reflect.has(ref.value, key),
        defineProperty: (_, key, descriptor) => Reflect.defineProperty(ref.value, key, descriptor),
        deleteProperty: (_, key) => Reflect.deleteProperty(ref.value, key),
        getOwnPropertyDescriptor: (_, key) => Reflect.getOwnPropertyDescriptor(ref.value, key),
        ownKeys: () => Reflect.ownKeys(ref.value),
        getPrototypeOf: () => Reflect.getPrototypeOf(ref.value),
    })
}
